
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Package, PackageOpen, PackagePlus, Zap } from "lucide-react";
import { useProfileStorage } from "@/hooks/useProfileStorage";
import { useTranslation } from "react-i18next";

const SubscriptionPlansOverview = () => {
  const {
    subscriptionPlans,
    storageUsage,
    formatStorageSize,
    isLoading
  } = useProfileStorage();
  
  // Debug logging to see which plans are loaded
  console.log('Loaded subscription plans:', subscriptionPlans);
  const {
    t,
    i18n
  } = useTranslation('profile');

  if (isLoading) {
    return <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(3)].map((_, i) => <Card key={i} className="animate-pulse uplivion-card">
            <CardContent className="p-6 space-y-4">
              <div className="h-4 bg-muted rounded w-3/4"></div>
              <div className="h-8 bg-muted rounded w-1/2"></div>
              <div className="space-y-2">
                <div className="h-3 bg-muted rounded"></div>
                <div className="h-3 bg-muted rounded w-2/3"></div>
              </div>
            </CardContent>
          </Card>)}
      </div>;
  }

  const isEnglish = i18n.language === 'en';
  const currentPlanId = storageUsage?.subscription_plan_id;

  const getPlanIcon = (planName: string) => {
    const name = planName.toLowerCase();
    if (name.includes('kostenloses') || name.includes('free')) return <Package className="w-5 h-5" />;
    if (name.includes('mini')) return <PackageOpen className="w-5 h-5" />;
    if (name.includes('erweitertes') || name.includes('extended')) return <PackagePlus className="w-5 h-5" />;
    if (name.includes('overmind')) return <Zap className="w-5 h-5 text-primary" />;
    // Fallback for any other plan names
    return <Package className="w-5 h-5" />;
  };

  const formatPrice = (cents: number) => {
    if (cents === 0) return t('plans.free', 'Kostenlos');
    return `${(cents / 100).toFixed(2)} €/${t('plans.month', 'Monat')}`;
  };

  return <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-2xl font-bold mb-2">{t('plans.title', 'Wählen Sie Ihren Plan')}</h3>
        <p className="text-muted-foreground">
          {t('plans.subtitle', 'Finden Sie den perfekten Plan für Ihre Bedürfnisse')}
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {subscriptionPlans?.map(plan => {
        const isCurrentPlan = plan.id === currentPlanId;
        const planName = isEnglish ? plan.name_en || plan.name : plan.name;
        const planDescription = isEnglish ? plan.description_en || plan.description : plan.description;
        return <Card key={plan.id} className={`relative uplivion-card ${isCurrentPlan ? 'ring-2 ring-primary' : ''} ${planName.toLowerCase().includes('overmind') ? 'border-primary shadow-lg' : ''}`}>
              {isCurrentPlan && <Badge className="absolute -top-2 left-1/2 -translate-x-1/2">
                  {t('plans.current', 'Aktueller Plan')}
                </Badge>}
              
              <CardHeader className="pb-4">
                <div className="flex items-center gap-2 mb-2">
                  {getPlanIcon(planName)}
                  <CardTitle className="text-lg">{planName}</CardTitle>
                  {planName.toLowerCase().includes('overmind') && (
                    <Badge variant="secondary" className="text-xs bg-primary/10 text-primary">
                      Premium
                    </Badge>
                  )}
                </div>
                <div className="text-xl font-bold">
                  {formatPrice(plan.price_monthly_cents)}
                </div>
                {planDescription && <p className="text-sm text-muted-foreground">{planDescription}</p>}
              </CardHeader>

              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span className="text-sm">
                      {formatStorageSize(plan.storage_limit_mb)} {t('plans.storage', 'Speicher')}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span className="text-sm">
                      {plan.max_entries ? `${plan.max_entries} ${t('plans.entries', 'Einträge')}` : t('plans.unlimitedEntries', 'Unbegrenzte Einträge')}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span className="text-sm">
                      {plan.max_images ? `${plan.max_images} ${t('plans.images', 'Bilder')}` : t('plans.unlimitedImages', 'Unbegrenzte Bilder')}
                    </span>
                  </div>
                </div>

                <Button className="w-full" variant={isCurrentPlan ? "outline" : "default"} disabled={isCurrentPlan}>
                  {isCurrentPlan ? t('plans.currentPlan', 'Aktueller Plan') : plan.price_monthly_cents === 0 ? t('plans.getStarted', 'Loslegen') : t('plans.upgrade', 'Upgraden')}
                </Button>
              </CardContent>
            </Card>;
      })}
      </div>
    </div>;
};

export default SubscriptionPlansOverview;
